import {Component, EventEmitter, OnInit, Output, AfterViewInit} from '@angular/core';
import {FieldsService} from '../../core/fields/fields.service';
import {UtilsService} from '../../core/utils/utils.service';
import {MapService} from '../../core/map/map.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AccountsService} from '../../core/accounts/accounts.service';

@Component({
  selector: 'app-visualize',
  templateUrl: './visualize.component.html',
  styleUrls: ['./visualize.component.scss']
})
export class VisualizeComponent implements OnInit, AfterViewInit {

  @Output() close = new EventEmitter();

  minColor = '#fff';

  maxColor = '#00f';

  minValue: number;

  maxValue: number;

  colorValues: any[];

  fieldValues: any[];

  selectedFieldValue: any;

  checkinSelected: boolean;

  accountsWithCheckins: any[] = [];

  maxPeriod: number;

  checkinRange: number[];

  constructor(private fieldsService: FieldsService,
              private accountsService: AccountsService,
              private utilsService: UtilsService,
              private mapService: MapService) { }

  ngAfterViewInit() {
    this.getFields(true);

    this.accountsService.editFieldValue
      .subscribe(() => {
        this.getFields();
      });

    this.fieldsService.createField
      .subscribe(() => {
        this.getFields();
      });

    this.fieldsService.deleteField
      .subscribe(() => {
        this.getFields();
      });
  }

  ngOnInit() {
    this.accountsService.listWithCheckins()
      .subscribe((accounts: any[]) => {
        accounts.forEach(a => {
          this.accountsWithCheckins.push({
            account: a,
            lastCheckin: a.checkins.length ? moment().diff(moment.max(a.checkins.map(c => moment(c.date))), 'days') : undefined
          });
        });
        this.maxPeriod = _.max(this.accountsWithCheckins.map(a => a.lastCheckin));

        this.checkinRange = [0, this.maxPeriod];
      });
  }

  getFields(init = false) {
    this.fieldsService.listFieldValues()
      .subscribe((fieldValues: any[]) => {
        this.fieldValues = fieldValues.filter(fv => fv.field.filterable && fv.values.length)
          .map(fv => {
            if (init) {
              if (fv.field.type === 'text') {
                fv.values = fv.values.map(v => {
                  v.active = true;
                  return v;
                });
              } else {
                fv.field.min = null;
                fv.field.max = null;
              }

            } else {
              if (fv.field.type === 'text') {
                fv.values = fv.values.map(v => {
                  const foundValue = this.getValue(v._id);
                  if (foundValue) {
                    v.active = foundValue.active;
                  } else {
                    v.active = true;
                  }
                  return v;
                });
              } else {
                const foundFieldValue = this.fieldValues.find(fv2 => fv._id === fv2._id);
                if (foundFieldValue) {
                  fv.field.min = foundFieldValue.field.min;
                  fv.field.max = foundFieldValue.field.max;
                } else {
                  fv.field.min = null;
                  fv.field.max = null;
                }
              }
            }
            return fv;
          });

        this.filterAccounts();
      }, (err) => {
        alert(err.error.message || 'An error occurred');
      });
  }

  getValue(id) {
    const allValues = _.flattenDeep(this.fieldValues.map(fv => fv.values));
    return allValues.find(v => v._id === id);
  }

  generateColors() {
    this.colorValues = [];

    if (this.selectedFieldValue.field.type === 'text') {
      this.selectedFieldValue.values.forEach(v => {
        this.colorValues.push({
          value: v.value,
          accounts: v.accounts,
          color: this.utilsService.stringToColor(v._id)
        });
      });
    } else {
      this.minValue = _.min(this.selectedFieldValue.values.map(v => +v.value));
      this.maxValue = _.max(this.selectedFieldValue.values.map(v => +v.value));

      const steps = this.maxValue - this.minValue;

      this.selectedFieldValue.values.forEach(v => {
        this.colorValues.push({
          value: v.value,
          accounts: v.accounts,
          color: this.utilsService.getInterpolatedColor(this.minColor, this.maxColor, steps, v.value - this.minValue)
        });
      });
    }
  }

  getColors(fieldValue) {
    if (this.isSelected(fieldValue)) {
      return this.colorValues;
    } else {
      return undefined;
    }
  }

  toggleField(e, field) {
    if (this.selectedFieldValue && this.selectedFieldValue === field) {
      this.selectedFieldValue = null;
      this.colorValues = [];
    } else {
      this.colorValues = [];
      this.selectedFieldValue = field;
      this.checkinSelected = false;
      this.generateColors();
    }
    this.mapService.setColors(this.colorValues);

    e.preventDefault();
  }

  isSelected(field) {
    return this.selectedFieldValue && this.selectedFieldValue.field._id === field.field._id;
  }

  onToggle() {
    this.filterAccounts();
  }

  filterAccounts() {
    const textFields = this.fieldValues.filter(fv => fv.field.type === 'text');
    const numberFields = this.fieldValues.filter(fv => fv.field.type === 'numeric');

    const textExcludedAccounts = _.flattenDeep(textFields.map(fv => {
      const inactiveValues = fv.values.filter(v => !v.active);
      return inactiveValues.map(av => av.accounts);
    }));

    const numberExcludedAccounts =  _.flattenDeep(numberFields.map(fv => {
      if (!fv.field.min && !fv.field.max) {
        return [];
      }
      const inactiveValues = fv.values.filter(v => (fv.field.min && v.value < fv.field.min) || (fv.field.max && v.value > fv.field.max));
      return inactiveValues.map(av => av.accounts);
    }));

    let checkinFilterAccounts;
    const min = this.checkinRange[0];
    const max = this.checkinRange[1];
    if (this.isCheckinDefault(min, max)) {
      checkinFilterAccounts = [];
    } else {
      checkinFilterAccounts = this.accountsWithCheckins.filter(a => {
        if (!a.lastCheckin) {
          return true;
        }
        return a.lastCheckin < min || a.lastCheckin > max;
      }).map(a => a.account._id);
    }
    this.mapService.filterAccounts(textExcludedAccounts.concat(numberExcludedAccounts).concat(checkinFilterAccounts));
  }

  resetFilters() {
    this.setCheckinToDefault();
    this.getFields(true);
  }

  isResetAvailable() {

    if (!this.fieldValues) {
      return false;
    }

    const textFields = this.fieldValues.filter(fv => fv.field.type === 'text');
    const numberFields = this.fieldValues.filter(fv => fv.field.type === 'numeric');

    const textFieldsToReset = textFields.some(fv => {
      return fv.values.some(v => !v.active);
    });

    const numberFieldsToReset = numberFields.some(fv => {
      if (!fv.field.min && !fv.field.max) {
        return false;
      }
      const minValue = _.min(fv.values.map(v => +v.value));
      const maxValue = _.max(fv.values.map(v => +v.value));

      return (fv.field.min && minValue !== fv.field.min) || (fv.field.max && maxValue !== fv.field.max);
    });

    return textFieldsToReset || numberFieldsToReset || (this.checkinRange[0] > 0 || this.checkinRange[1] < this.maxPeriod);

  }

  isText(fieldValue) {
    return fieldValue.field.type === 'text';
  }

  isNumber(fieldValue) {
    return fieldValue.field.type === 'numeric';
  }

  coloriseByCheckin() {
    this.colorValues = [];

    this.accountsWithCheckins.forEach(a => {
      if (!a.lastCheckin) {
        return;
      }
      this.colorValues.push({
        accounts: [a.account._id],
        color: this.utilsService.getInterpolatedColor(this.minColor, this.maxColor, this.maxPeriod, a.lastCheckin)
      });
    });

    this.mapService.setColors(this.colorValues);
  }

  selectCheckin(e) {
    this.selectedFieldValue = null;
    this.checkinSelected = true;
    this.coloriseByCheckin();
    e.preventDefault();
  }

  isCheckinSelected() {
    return this.checkinSelected;
  }

  onRangeChange(e) {
    this.filterAccounts();
  }

  setCheckinToDefault(e?) {
    this.checkinRange = [0, this.maxPeriod];
    if (e) {
      e.preventDefault();
    }
  }

  isCheckinDefault(from, to) {
    return from === 0 && to === this.maxPeriod;
  }

  showFieldFilter(fieldValue) {
    return fieldValue.field.type !== 'numeric' || (_.union(fieldValue.values.map(v => v.value)).length > 1);
  }

}



// WEBPACK FOOTER //
// ./client/app/components/visualize/visualize.component.ts